import React, { useState, useRef, useMemo } from "react";
import {
  Card,
  Box,
  Typography,
  Avatar,
  Grid,
  useTheme,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Switch,
  FormControlLabel,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";
import {
  Settings as SettingsIcon,
  Print as PrintIcon,
  FormatSize as FormatSizeIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Info as InfoIcon,
  Crop as CropIcon,
  Preview as PreviewIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Close as CloseIcon,
  QrCodeScanner as QrCodeScannerIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { printHtml } from "../../utils/htmlToPrint";

// Utility: Get contrast color for text
const getContrastColor = (hexColor) => {
  const color = hexColor.replace("#", "");
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
};

// ─── Styled Components ──────────────────────────────────────────────

const PVCIDCardWrapper = styled(Card)(({ theme, cardColor, isLandscape, showBleed, cardHeight }) => ({
  width: isLandscape ? 380 : 320,
  height: cardHeight || (isLandscape ? 480 : 500),
  minHeight: isLandscape ? 380 : 400,
  borderRadius: "12px",
  overflow: "hidden",
  position: "relative",
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  backgroundColor: "#ffffff",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 30px 60px rgba(0,0,0,0.25)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    border: "2px dashed rgba(0,0,0,0.1)",
    borderRadius: "14px",
    pointerEvents: "none",
    display: showBleed ? "block" : "none",
  },
}));

const PVCGradientHeader = styled(Box)(({ color }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: 170,
  background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.8)})`,
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background:
      "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
    opacity: 0.3,
  },
}));

const PVCCurvedShape = styled(Box)({
  position: "absolute",
  top: 120,
  left: 0,
  width: "100%",
  height: 100,
  backgroundColor: "white",
  borderTopLeftRadius: "50% 100%",
  borderTopRightRadius: "50% 100%",
});

const PVCAvatar = styled(Avatar)({
  width: 100,
  height: 100,
  border: "5px solid white",
  boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
  transition: "transform 0.3s ease",
  "&:hover": { transform: "scale(1.05)" },
});

const PVCRoleChip = styled(Chip)(({ bgcolor, textcolor }) => ({
  backgroundColor: `${bgcolor} !important`,
  color: `${textcolor} !important`,
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "16px",
  height: '28px',
  '& .MuiChip-label': {
    color: `${textcolor} !important`,
    padding: '0 12px',
    fontSize: '0.7rem',
    fontWeight: 600,
  },
  '&:hover': {
    transform: "scale(1.02)",
    backgroundColor: `${bgcolor} !important`,
  },
  // Force all states
  '&.MuiChip-root': {
    color: `${textcolor} !important`,
  },
  '& .MuiChip-label.MuiChip-label': {
    color: `${textcolor} !important`,
  },
  // For PDF rendering
  '&[data-pdf="true"]': {
    backgroundColor: `${bgcolor} !important`,
    color: `${textcolor} !important`,
    '& .MuiChip-label': {
      color: `${textcolor} !important`,
    },
  },
}));


const PVCInfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "1px 0",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  "&:last-child": { borderBottom: "none" },
}));

const ControlsPanel = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 1.5,
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "1200px",
  margin: "auto",
  mb: 3,
  p: 2,
  borderRadius: 16,
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: "blur(20px)",
  "@media print": { display: "none" },
}));

// ─── Field Configuration ────────────────────────────────────────────

const FIELD_GROUPS = {
  personal: {
    label: "Personal Information",
    icon: <PersonIcon />,
    fields: [
      { key: "firstName", label: "First Name" },
      { key: "middleName", label: "Middle Name" },
      { key: "lastName", label: "Last Name" },
      { key: "dateOfBirth", label: "Date of Birth" },
    ],
  },
  contact: {
    label: "Contact Details",
    icon: <InfoIcon />,
    fields: [
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "addressDTO", label: "Address" },
      { key: "aadhaarNumber", label: "Aadhaar Number" },
    ],
  },
  professional: {
    label: "Professional Details",
    icon: <WorkIcon />,
    fields: [
      { key: "employeeCode", label: "Employee Code" },
      { key: "specialization", label: "Specialization" },
      { key: "experience", label: "Experience" },
      { key: "qualification", label: "Qualification" },
      { key: "degree", label: "Degree" },
      { key: "branch", label: "Branch" },
    ],
  },
};

const DEFAULT_VISIBLE_FIELDS = {
  firstName: true,
  middleName: false,
  lastName: true,
  dateOfBirth: false,
  phone: true,
  email: false,
  addressDTO: true,
  aadhaarNumber: false,
  employeeCode: true,
  specialization: true,
  experience: false,
  qualification: false,
  degree: false,
  branch: false,
};

// ─── Main Component ──────────────────────────────────────────────────

export default function ModernIDCard({ data, school, colors = {} }) {
  const theme = useTheme();
  const items = Array.isArray(data) ? data : [];
  const cardRef = useRef(null);

  // ── State ──
  const [showSettings, setShowSettings] = useState(false);
  const [cardColor, setCardColor] = useState(colors?.default || theme.palette.primary.main);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showBorder, setShowBorder] = useState(true);
  const [cardScale, setCardScale] = useState(1);
  const [code, setCode] = useState("QRCode");
  const [showBarcode, setShowBarcode] = useState(true);
  const [showPhoto, setShowPhoto] = useState(true);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const [pvcSize, setPvcSize] = useState("CR80");
  const [fontcolor, setFontcolor] = useState("#FFFFFF");
  const [showBleed, setShowBleed] = useState(false);
  const [pvcStyle, setPvcStyle] = useState("modern");
  const [showRole, setShowRole] = useState(true);
  const [cardOpacity, setCardOpacity] = useState(1);
  const [fontSize, setFontSize] = useState(12);
  const [showWatermark, setShowWatermark] = useState(true);
  const [dynamicHeight, setDynamicHeight] = useState(true); // NEW: toggle for dynamic height

  const [visibleFields, setVisibleFields] = useState(DEFAULT_VISIBLE_FIELDS);

  // ── Helpers ──
  const getCardColor = (emp) => colors[emp.id] || colors.default || cardColor;
  const getFontColor = (emp) => fontcolor || getContrastColor(getCardColor(emp));

  // Count visible fields
  const visibleFieldsCount = useMemo(() => {
    return Object.values(visibleFields).filter(Boolean).length;
  }, [visibleFields]);

  // Calculate dynamic height based on visible fields
  const getCardHeight = useMemo(() => {
    if (!dynamicHeight) return isLandscape ? 480 : 500;

    // Base height without fields
    let baseHeight = showPhoto ? 210 : 130;
    // Each field adds about 22px
    const fieldHeight = Math.min(visibleFieldsCount, 7) * 22;
    // QR/Barcode height
    const codeHeight = showBarcode ? 50 : 0;
    // Watermark height
    const watermarkHeight = showWatermark ? 40 : 0;
    // Padding
    const padding = 40;

    const totalHeight = baseHeight + fieldHeight + codeHeight + watermarkHeight + padding;

    // Ensure minimum and maximum height
    const minHeight = isLandscape ? 380 : 400;
    const maxHeight = isLandscape ? 520 : 550;

    return Math.min(Math.max(totalHeight, minHeight), maxHeight);
  }, [visibleFieldsCount, showPhoto, showBarcode, showWatermark, dynamicHeight, isLandscape]);

  const formatValue = (emp, key) => {
    const val = emp[key];
    if (val === null || val === undefined) return "N/A";
    if (["dateOfBirth", "joiningDate"].includes(key)) {
      return new Date(val).toLocaleDateString("en-GB");
    }
    if (key === "middleName") {
      return [emp?.middleName, emp?.lastName].filter(Boolean).join(" ");
    }

    if (key === "addressDTO") {
      const addr = val;
      return [addr?.addressLine1, addr?.city, addr?.pincode, addr?.state].filter(Boolean).join(", ");
    }
    return String(val);
  };

  const toggleField = (key) =>
    setVisibleFields((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleGroup = (groupKey, value) => {
    const newState = {};
    FIELD_GROUPS[groupKey].fields.forEach((f) => (newState[f.key] = value));
    setVisibleFields((prev) => ({ ...prev, ...newState }));
  };

  const isGroupVisible = (groupKey) =>
    FIELD_GROUPS[groupKey].fields.some((f) => visibleFields[f.key]);

  // ── Actions ──
  const toggleLandscape = () => setIsLandscape((v) => !v);

  const handlePrint = () => {
    if (selectedCard?.id) {
      printHtml(selectedCard?.id);
    } else {
      printHtml("id-cards-container");
    }
    window.location.reload();
  };

  const handleDownloadPDF = async () => {
    const el = document.getElementById("id-cards-container");
    if (!el) return;

    try {
      // Store original styles
      const originalPadding = el.style.padding;
      const originalBg = el.style.backgroundColor;
      const originalOverflow = el.style.overflow;

      // Add padding to prevent cutting
      el.style.padding = '20px 20px 40px 20px';
      el.style.backgroundColor = '#ffffff';
      el.style.overflow = 'visible';

      // Get all cards and fix their heights
      const cards = el.querySelectorAll('.MuiPaper-root');
      cards.forEach(card => {
        card.style.height = 'auto';
        card.style.minHeight = '420px';
        card.style.marginBottom = '20px';
        card.style.overflow = 'visible';
      });

      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        scrollY: 0,
        windowHeight: el.scrollHeight + 100,
        windowWidth: el.scrollWidth,
        height: el.scrollHeight + 100,
        onclone: (clonedDoc) => {
          // Fix styles in cloned document
          const clonedEl = clonedDoc.getElementById("id-cards-container");
          if (clonedEl) {
            clonedEl.style.padding = '20px 20px 40px 20px';
            clonedEl.style.overflow = 'visible';
          }
          // Fix cards in clone
          const clonedCards = clonedEl?.querySelectorAll('.MuiPaper-root') || [];
          clonedCards.forEach(card => {
            card.style.height = 'auto';
            card.style.minHeight = '420px';
            card.style.marginBottom = '20px';
            card.style.overflow = 'visible';
            // Fix chip colors in clone
            const chips = card.querySelectorAll('.MuiChip-root');
            chips.forEach(chip => {
              const bgColor = chip.getAttribute('data-bgcolor') || chip.style.backgroundColor;
              const textColor = chip.getAttribute('data-textcolor') || chip.style.color;
              if (bgColor) chip.style.backgroundColor = bgColor;
              if (textColor) {
                chip.style.color = textColor;
                const label = chip.querySelector('.MuiChip-label');
                if (label) label.style.color = textColor;
              }
            });
          });
        }
      });

      // Restore original styles
      el.style.padding = originalPadding || '';
      el.style.backgroundColor = originalBg || '';
      el.style.overflow = originalOverflow || '';

      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = 210;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(img, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("id-cards.pdf");

    } catch (err) {
      console.error("PDF error:", err);
      alert("Error generating PDF. Please try again.");
      // Restore styles on error
      el.style.padding = '';
      el.style.backgroundColor = '';
      el.style.overflow = '';
    }
  };

  const handlePreview = (emp) => {
    setSelectedCard(emp);
    setPreviewDialog(true);
  };

  // ── Render Card ──
  const renderIDCard = (emp, index) => {
    const color = getCardColor(emp);
    const fontColor = getFontColor(emp);
    const fullName = [emp.firstName, emp.lastName].filter(Boolean).join(" ");
    const role = emp.experience < 1 ? "Teacher" : `Senior ${emp.specialization || ""} Teacher`;

    const infoFields = [
      { key: "employeeCode", label: "ID" },
      { key: "middleName", label: "Father/Husband" },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "specialization", label: "Specialization" },
      { key: "experience", label: "Experience" },
      { key: "qualification", label: "Qualification" },
      { key: "degree", label: "Degree" },
      { key: "dateOfBirth", label: "DOB" },
      { key: "aadhaarNumber", label: "Aadhaar" },
      { key: "addressDTO", label: "Address" },
      { key: "branch", label: "Branch" },
    ].filter((f) => visibleFields[f.key]);

    return (
      <PVCIDCardWrapper
        key={emp.id || index}
        cardColor={color}
        isLandscape={isLandscape}
        showBleed={showBleed}
        cardHeight={getCardHeight}
        sx={{
          transform: `scale(${cardScale})`,
          border: showBorder ? `2px solid ${color}` : "none",
          opacity: cardOpacity,
          "& .MuiTypography-root": { fontSize: `${fontSize}px` },
        }}
        ref={cardRef}
      >
        {/* Bleed marks */}
        {showBleed && (
          <>
            {["top-left", "top-right", "bottom-left", "bottom-right"].map((pos, i) => {
              const isTop = i < 2;
              const isLeft = i % 2 === 0;
              return (
                <Box
                  key={pos}
                  sx={{
                    position: "absolute",
                    [isTop ? "top" : "bottom"]: 2,
                    [isLeft ? "left" : "right"]: 2,
                    width: 6,
                    height: 6,
                    borderTop: isTop ? "2px solid red" : "none",
                    borderBottom: isTop ? "none" : "2px solid red",
                    borderLeft: isLeft ? "2px solid red" : "none",
                    borderRight: isLeft ? "none" : "2px solid red",
                    zIndex: 10,
                  }}
                />
              );
            })}
          </>
        )}

        {/* Header */}
        <PVCGradientHeader color={color}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              width: "94%",
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              color: fontColor,
              zIndex: 1,
            }}
          >

            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                p: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <img
                src={school?.logoUrl}
                alt="Logo"
                width={isLandscape ? 45 : 50}
                height={isLandscape ? 40 : 45}
                style={{ objectFit: "contain" }}
              />
            </Box>

            <Box sx={{ color: fontColor, textAlign: "center", flex: 1, ml: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: isLandscape ? "0.8rem" : "0.85rem", color: fontColor }}>
                {school?.schoolName || "School Name"}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2, fontSize: isLandscape ? "0.5rem" : "0.55rem", opacity: 0.9, color: fontColor }}>
                {[school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state]
                  .filter(Boolean)
                  .join(", ")}
                {school?.contactPhone && ` | Tel: ${school.contactPhone}`}
              </Typography>
            </Box>
          </Box>
        </PVCGradientHeader>

        <PVCCurvedShape />

        {showPhoto && (
          <Box sx={{ position: "absolute", top: 82, width: "100%", display: "flex", justifyContent: "center", zIndex: 2 }}>
            <PVCAvatar src={emp.photo} alt={fullName}>
              {fullName?.charAt(0) || "S"}
            </PVCAvatar>
          </Box>
        )}

        <Box sx={{ position: "absolute", top: showPhoto ? 210 : 130, width: "90%", textAlign: "center", px: 2 }}>
          <Typography variant="h6" fontWeight={800} sx={{ fontSize: isLandscape ? "0.9rem" : "0.95rem" }}>
            {fullName || "Employee Name"}
          </Typography>

          {showRole && (
            <Box sx={{ mt: 0.5, mb: 1 }}>
              <PVCRoleChip
                label={role}
                size="small"
                bgcolor={color}
                textcolor={fontcolor}
                data-bgcolor={color}
                data-textcolor={fontcolor}
                style={{
                  backgroundColor: color,
                  color: fontcolor,
                }}
                sx={{
                  backgroundColor: `${color} !important`,
                  color: `${fontcolor} !important`,
                  '& .MuiChip-label': {
                    color: `${fontcolor} !important`,
                  },
                }}
              />
            </Box>
          )}
          
          <Box sx={{ textAlign: "left", mt: 1.5, fontSize: 11 }}>
            {infoFields.slice(0, 7).map((field) => (
              <PVCInfoRow key={field.key}>
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: isLandscape ? "0.55rem" : "0.6rem" }}>
                  {field.label}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: isLandscape ? "0.55rem" : "0.6rem", textAlign: "right", maxWidth: "55%" }} noWrap>
                  {formatValue(emp, field.key)}
                </Typography>
              </PVCInfoRow>
            ))}
          </Box>
        </Box>

        {showBarcode && (
          <Box sx={{ position: "absolute", bottom: 8, right: 12, display: "flex", alignItems: "center", gap: 0.5 }}>
            {code === "QRCode" && (
              <Box sx={{ bgcolor: "white", p: 0.3, borderRadius: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <QRCode value={`${emp.id}#${emp.employeeCode}`} size={45} />
              </Box>
            )}
            {code === "Barcode" && (
              <Box sx={{ bgcolor: "white", p: 0.3, borderRadius: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <Barcode width={1} height={40} value={`${emp.id}#${emp.employeeCode}`} displayValue={false} />
              </Box>
            )}
          </Box>
        )}

        {showWatermark && (
          <Box sx={{ position: "absolute", bottom: 40, left: 8, opacity: 0.03, fontSize: 36, fontWeight: "bold", color, pointerEvents: "none", transform: "rotate(-15deg)" }}>
            {school?.schoolName?.charAt(0) || "S"}
          </Box>
        )}
      </PVCIDCardWrapper>
    );
  };

  // ── Render ──
  return (
    <Box id="emp-id-cards-root">
      {/* Controls */}
      <ControlsPanel elevation={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
            <QrCodeScannerIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Code:
          </Typography>
          <Select
            value={code}
            onChange={(e) => setCode(e.target.value)}
            size="small"
            sx={{ minWidth: 100, fontSize: "0.75rem" }}
          >
            <MenuItem value="Barcode">Bar code</MenuItem>
            <MenuItem value="QRCode">QR Code</MenuItem>
          </Select>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
            Font Color:
          </Typography>
          <input
            type="color"
            value={fontcolor}
            onChange={(e) => setFontcolor(e.target.value)}
            style={{ width: 28, height: 28, padding: 0, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
          />
        </Box>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<FormatSizeIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <FormControlLabel
          control={
            <Switch
              checked={dynamicHeight}
              onChange={(e) => setDynamicHeight(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={<Typography variant="caption" fontSize="0.7rem">Auto Height</Typography>}
        />

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings((v) => !v)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title="Print Cards">
          <IconButton size="small" onClick={handlePrint}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download PDF">
          <IconButton size="small" onClick={handleDownloadPDF}>
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Preview Card">
          <IconButton size="small" onClick={() => handlePreview(items[0])}>
            <PreviewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Toggle Bleed Marks">
          <IconButton size="small" onClick={() => setShowBleed((v) => !v)} color={showBleed ? "primary" : "default"}>
            <CropIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ControlsPanel>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper sx={{ maxWidth: "1200px", margin: "auto", mb: 3, p: 2.5, bgcolor: "#f8f9fa", borderRadius: 3, "@media print": { display: "none" } }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                PVC Card Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Card Size</InputLabel>
                    <Select value={pvcSize} onChange={(e) => setPvcSize(e.target.value)} label="Card Size">
                      <MenuItem value="CR80">CR-80 (Standard)</MenuItem>
                      <MenuItem value="CR80_ALT">CR-80 (Alternate)</MenuItem>
                      <MenuItem value="SQUARE">Square</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Card Style</InputLabel>
                    <Select value={pvcStyle} onChange={(e) => setPvcStyle(e.target.value)} label="Card Style">
                      <MenuItem value="modern">Modern</MenuItem>
                      <MenuItem value="classic">Classic</MenuItem>
                      <MenuItem value="minimal">Minimal</MenuItem>
                      <MenuItem value="premium">Premium</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Card Scale: {cardScale.toFixed(1)}x
                  </Typography>
                  <Slider value={cardScale} min={0.8} max={1.2} step={0.05} onChange={(_, v) => setCardScale(v)} size="small" sx={{ width: "90%" }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" display="block" gutterBottom>
                    Font Size: {fontSize}px
                  </Typography>
                  <Slider value={fontSize} min={9} max={16} step={0.5} onChange={(_, v) => setFontSize(v)} size="small" sx={{ width: "90%" }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel control={<Switch checked={showBorder} onChange={(e) => setShowBorder(e.target.checked)} size="small" />} label={<Typography variant="caption">Show Border</Typography>} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel control={<Switch checked={showPhoto} onChange={(e) => setShowPhoto(e.target.checked)} size="small" />} label={<Typography variant="caption">Show Photo</Typography>} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel control={<Switch checked={showRole} onChange={(e) => setShowRole(e.target.checked)} size="small" />} label={<Typography variant="caption">Show Role</Typography>} />
                </Grid>
                {/* <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel control={<Switch checked={showBarcode} onChange={(e) => setShowBarcode(e.target.checked)} size="small" />} label={<Typography variant="caption">Show Code</Typography>} />
                </Grid> */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel control={<Switch checked={showWatermark} onChange={(e) => setShowWatermark(e.target.checked)} size="small" />} label={<Typography variant="caption">Watermark</Typography>} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dynamicHeight}
                        onChange={(e) => setDynamicHeight(e.target.checked)}
                        size="small"
                        color="primary"
                      />
                    }
                    label={<Typography variant="caption">Auto Height</Typography>}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Field Visibility */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Field Visibility
              </Typography>
              {Object.entries(FIELD_GROUPS).map(([groupKey, group]) => (
                <Accordion key={groupKey} sx={{ "&:before": { display: "none" }, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {group.icon}
                      <Typography variant="caption" fontWeight={600}>{group.label}</Typography>
                      <Chip size="small" label={isGroupVisible(groupKey) ? "Visible" : "Hidden"} color={isGroupVisible(groupKey) ? "success" : "default"} variant="outlined" sx={{ ml: 1, height: 20, fontSize: "0.6rem" }} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <Button size="small" onClick={() => toggleGroup(groupKey, !group.fields.every((f) => visibleFields[f.key]))} sx={{ fontSize: "0.65rem" }}>
                        {group.fields.every((f) => visibleFields[f.key]) ? "Hide All" : "Show All"}
                      </Button>
                    </Box>
                    <FormGroup>
                      <Grid container spacing={0.5}>
                        {group.fields.map((field) => (
                          <Grid item xs={12} sm={6} md={4} key={field.key}>
                            <FormControlLabel
                              control={<Checkbox checked={visibleFields[field.key] || false} onChange={() => toggleField(field.key)} size="small" color="primary" />}
                              label={<Typography variant="caption">{field.label}</Typography>}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Cards Grid */}
      <Box id="id-cards-container">
        <Grid container spacing={3} justifyContent="center" mt={3}>
          {items.map((emp, i) => (
            <Grid item key={emp.id || i} xs={12} sm={6} md={4} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
              {renderIDCard(emp, i)}
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Card Preview</Typography>
            <IconButton onClick={() => setPreviewDialog(false)} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 3 }} id={selectedCard?.id}>
          {selectedCard && renderIDCard(selectedCard)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
          <Button variant="contained" onClick={handlePrint}><PrintIcon fontSize="small" sx={{ mr: 1 }} /> Print</Button>
        </DialogActions>
      </Dialog>

      {/* Empty State */}
      {items.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8, px: 2, bgcolor: "#f8f9fa", borderRadius: 4 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>No ID Cards to Display</Typography>
          <Typography variant="body2" color="textSecondary">Please add employee data to generate ID cards.</Typography>
        </Box>
      )}
    </Box>
  );
}